import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import GoogleIcon from "@mui/icons-material/Google";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchIcon from "@mui/icons-material/Search";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AppShell from "../components/AppShell";
import HistoryDrawer from "../components/HistoryDrawer";
import ApiService from "../services/ApiService";
import IntegrationsService from "../services/IntegrationsService";
import InsightsService from "../services/InsightsService";

const SYNC_INTERVALS = [
  { value: "manual", label: "Manual only" },
  { value: "daily", label: "Daily · 2 AM UTC" },
  { value: "hourly", label: "Every hour" },
];

const OAUTH_START = {
  meta: () => IntegrationsService.startMetaOAuth(),
  google: () => IntegrationsService.startGoogleOAuth(),
  search_console: () => IntegrationsService.startSearchConsoleOAuth(),
  google_ads: () => IntegrationsService.startGoogleAdsOAuth(),
  linkedin: () => IntegrationsService.startLinkedInOAuth(),
  tiktok: () => IntegrationsService.startTikTokOAuth(),
};

function fmtDateTime(value) {
  if (!value) return "Never";
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(value); }
}

function fmtDuration(started, finished) {
  if (!started || !finished) return null;
  const ms = new Date(finished) - new Date(started);
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

const PROVIDER_META = {
  meta: { color: "#1877F2", bg: "#EFF6FF", label: "Meta" },
  google: { color: "#EA4335", bg: "#FEF2F2", label: "Google" },
  search_console: { color: "#34A853", bg: "#F0FDF4", label: "Search Console" },
  google_ads: { color: "#FBBC04", bg: "#FFFBEB", label: "Google Ads" },
  linkedin: { color: "#0A66C2", bg: "#EFF6FF", label: "LinkedIn" },
  tiktok: { color: "#010101", bg: "#F8FAFC", label: "TikTok" },
  instagram_business: { color: "#E1306C", bg: "#FFF0F5", label: "Instagram" },
};

function ProviderIcon({ provider, kind, size = 20 }) {
  const sx = { fontSize: size };
  if (provider === "meta" && kind === "instagram_business") return <InstagramIcon sx={{ ...sx, color: "#E1306C" }} />;
  if (provider === "meta" && kind === "facebook_ads_account") return <CampaignOutlinedIcon sx={{ ...sx, color: "#1877F2" }} />;
  if (provider === "meta") return <FacebookIcon sx={{ ...sx, color: "#1877F2" }} />;
  if (provider === "google") return <GoogleIcon sx={{ ...sx, color: "#EA4335" }} />;
  if (provider === "search_console") return <SearchIcon sx={{ ...sx, color: "#34A853" }} />;
  if (provider === "google_ads") return <CampaignOutlinedIcon sx={{ ...sx, color: "#FBBC04" }} />;
  if (provider === "linkedin") return <LinkedInIcon sx={{ ...sx, color: "#0A66C2" }} />;
  if (provider === "tiktok") return <MusicNoteIcon sx={{ ...sx, color: "#010101" }} />;
  return null;
}

function kindLabel(kind) {
  const labels = {
    instagram_business: "Instagram Business",
    facebook_page: "Facebook Page",
    facebook_ads_account: "Meta Ads",
    ga4_property: "GA4 Property",
    search_console_site: "Search Console",
    google_ads_customer: "Google Ads",
    linkedin_ads_account: "LinkedIn Ads",
    tiktok_ads_account: "TikTok Ads",
  };
  return labels[kind] || kind;
}

function ProviderCard({ provider, connectedCount, isConnecting, onConnect }) {
  const disabled = !provider.configured || provider.comingSoon;
  const meta = PROVIDER_META[provider.provider] || {};

  return (
    <Paper
      sx={{
        p: 2.5,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.2s",
        "&:hover": !disabled ? { boxShadow: "0 4px 16px rgba(15,23,42,0.08)" } : {},
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Top accent */}
      {!disabled && (
        <Box
          sx={{
            position: "absolute",
            top: 0, left: 0, right: 0, height: 3,
            background: `linear-gradient(90deg, ${meta.color || "#5B5BD6"}, ${alpha(meta.color || "#5B5BD6", 0.3)})`,
            borderRadius: "12px 12px 0 0",
          }}
        />
      )}

      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar
          variant="rounded"
          sx={{ width: 44, height: 44, bgcolor: meta.bg || "#F1F5F9", mt: 0.25 }}
        >
          <ProviderIcon provider={provider.provider} size={22} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
            <Typography variant="h5" sx={{ color: "text.primary" }}>{provider.label}</Typography>
            {provider.comingSoon && (
              <Chip size="small" label="Soon" sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#F1F5F9", color: "#64748B" }} />
            )}
            {!provider.configured && !provider.comingSoon && (
              <Chip size="small" icon={<WarningAmberIcon sx={{ fontSize: "12px !important" }} />} label="Not configured" color="warning" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {provider.description}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ flex: 1 }} />

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2.5 }}>
        <Button
          variant={connectedCount > 0 ? "outlined" : "contained"}
          size="small"
          disabled={disabled || isConnecting}
          onClick={() => onConnect(provider.provider)}
          startIcon={
            isConnecting
              ? <CircularProgress size={13} color="inherit" />
              : connectedCount > 0
              ? <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
              : null
          }
          sx={{ flex: 1 }}
        >
          {isConnecting ? "Opening…" : connectedCount > 0 ? "Add account" : "Connect"}
        </Button>
        {connectedCount > 0 && (
          <Chip
            icon={<CheckIcon sx={{ fontSize: "13px !important" }} />}
            label={`${connectedCount} connected`}
            size="small"
            sx={{
              bgcolor: "#F0FDF4",
              color: "#10B981",
              border: "1px solid #BBF7D0",
              fontWeight: 600,
              fontSize: "0.7rem",
              height: 24,
            }}
          />
        )}
      </Stack>
    </Paper>
  );
}

function ConnectedRow({ row, onRefresh, onDisconnect, onHistory, onIntervalChange, busyId, updatingId }) {
  const isBusy = busyId === row.id;
  const isUpdating = updatingId === row.id;
  const meta = PROVIDER_META[row.provider] || {};
  const isError = row.status === "error";

  return (
    <Paper
      sx={{
        p: 0,
        overflow: "hidden",
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: "0 2px 8px rgba(15,23,42,0.06)" },
      }}
    >
      {/* Error banner */}
      {isError && row.lastError && (
        <Box
          sx={{
            px: 2.5,
            py: 1,
            bgcolor: "#FFF1F2",
            borderBottom: "1px solid #FECDD3",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 14, color: "error.main" }} />
          <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
            {row.lastError}
          </Typography>
        </Box>
      )}

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "center" }}
        sx={{ px: 2.5, py: 2 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Avatar
            src={row.avatarUrl || undefined}
            sx={{
              width: 40,
              height: 40,
              bgcolor: meta.bg || "#F1F5F9",
              border: "2px solid #E8EDF5",
            }}
          >
            <ProviderIcon provider={row.provider} kind={row.providerAccountKind} size={18} />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mb: 0.25 }}>
              <Typography variant="subtitle2" noWrap sx={{ color: "text.primary" }}>
                {row.displayName || row.providerAccountId}
              </Typography>
              <Chip
                size="small"
                label={kindLabel(row.providerAccountKind)}
                sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#F1F5F9", color: "#64748B", border: "none" }}
              />
              {isError ? (
                <Chip size="small" color="error" label="Error" sx={{ height: 18, fontSize: "0.65rem" }} />
              ) : (
                <Chip
                  size="small"
                  label="Active"
                  sx={{ height: 18, fontSize: "0.65rem", bgcolor: "#F0FDF4", color: "#10B981", border: "1px solid #BBF7D0" }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Last synced: {fmtDateTime(row.lastSyncedAt)}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Select
            size="small"
            value={row.syncInterval || "daily"}
            onChange={(e) => onIntervalChange(row.id, e.target.value)}
            disabled={isUpdating}
            sx={{ fontSize: "0.8rem", minWidth: 150, height: 34 }}
          >
            {SYNC_INTERVALS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.82rem" }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>

          <Button
            variant="outlined"
            size="small"
            startIcon={isBusy ? <CircularProgress size={13} /> : <RefreshIcon sx={{ fontSize: 15 }} />}
            disabled={isBusy}
            onClick={() => onRefresh(row.id)}
            sx={{ height: 34, whiteSpace: "nowrap" }}
          >
            Sync now
          </Button>

          <Tooltip title="Sync history" arrow>
            <IconButton
              size="small"
              onClick={() => onHistory(row)}
              disabled={isBusy}
              sx={{ width: 34, height: 34, color: "text.secondary" }}
            >
              <HistoryIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Disconnect" arrow>
            <IconButton
              size="small"
              color="error"
              disabled={isBusy}
              onClick={() => onDisconnect(row)}
              sx={{ width: 34, height: 34 }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

function SyncLogTable({ logs, loading }) {
  if (loading) return <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />;
  if (!logs || logs.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No sync runs yet. Accounts sync automatically based on their schedule.
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Started</TableCell>
            <TableCell>Trigger</TableCell>
            <TableCell align="right">Accounts</TableCell>
            <TableCell align="right">Success</TableCell>
            <TableCell align="right">Failed</TableCell>
            <TableCell align="right">Duration</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logs.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{fmtDateTime(row.startedAt)}</Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.trigger}
                  sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#F1F5F9", color: "#475569" }}
                />
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">{row.totalAccounts}</Typography>
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                  {row.successCount > 0 && (
                    <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: 15 }} />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "success.main" }}>
                    {row.successCount}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                  {row.failedCount > 0 && (
                    <ErrorOutlineIcon sx={{ color: "error.main", fontSize: 15 }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: row.failedCount > 0 ? "error.main" : "text.secondary" }}
                  >
                    {row.failedCount}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="right">
                <Typography variant="caption" color="text.secondary">
                  {fmtDuration(row.startedAt, row.finishedAt) || "—"}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

const PROVIDER_GROUPS = [
  { providers: ["meta"], label: "Meta (Facebook + Instagram)" },
  { providers: ["google", "search_console", "google_ads"], label: "Google" },
  { providers: ["linkedin"], label: "LinkedIn" },
  { providers: ["tiktok"], label: "TikTok" },
];

export default function IntegrationsView() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [providers, setProviders] = useState([]);
  const [connected, setConnected] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const [historyAccount, setHistoryAccount] = useState(null);
  const [snackbar, setSnackbar] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [providersList, connectedList, me] = await Promise.all([
        IntegrationsService.listProviders(),
        IntegrationsService.listConnected(),
        ApiService.getAccountMe().catch(() => ({ account: null })),
      ]);
      setProviders(Array.isArray(providersList) ? providersList : []);
      setConnected(Array.isArray(connectedList) ? connectedList : []);
      setAccount(me?.account ?? null);
    } catch (e) {
      if (e?.response?.status === 401) { navigate("/signin"); return; }
      setError(e?.response?.data?.message || e.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const refreshLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const data = await IntegrationsService.getSyncLogs(20);
      setSyncLogs(Array.isArray(data) ? data : []);
    } catch { setSyncLogs([]); }
    finally { setLogsLoading(false); }
  }, []);

  useEffect(() => { refresh(); refreshLogs(); }, [refresh, refreshLogs]);

  useEffect(() => {
    function onMessage(event) {
      const data = event?.data;
      if (!data || data.type !== "sm:oauth") return;
      const payload = data.payload || {};
      setConnectingProvider(null);
      if (payload.ok) {
        setSnackbar({
          severity: "success",
          message: `Connected ${payload.count || 0} ${payload.provider || ""} account(s).`,
        });
        refresh();
      } else {
        setSnackbar({ severity: "error", message: payload.error || "Connection failed." });
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [refresh]);

  function openOAuthPopup(url) {
    const w = 620, h = 740;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    const popup = window.open(url, "sm-oauth", `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) {
      setConnectingProvider(null);
      setSnackbar({ severity: "warning", message: "Popup blocked. Allow popups and try again." });
    }
  }

  async function handleConnect(provider) {
    const startFn = OAUTH_START[provider];
    if (!startFn) return;
    setConnectingProvider(provider);
    try {
      const { url } = await startFn();
      openOAuthPopup(url);
    } catch (e) {
      setConnectingProvider(null);
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Failed to start OAuth." });
    }
  }

  async function handleRefreshOne(id) {
    setBusyId(id);
    try {
      await InsightsService.refreshOne(id);
      setSnackbar({ severity: "success", message: "Synced successfully." });
      await Promise.all([refresh(), refreshLogs()]);
    } catch (e) {
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Sync failed." });
    } finally { setBusyId(null); }
  }

  async function handleDisconnect(row) {
    setBusyId(row.id);
    try {
      await IntegrationsService.disconnect(row.id);
      setSnackbar({ severity: "success", message: "Account disconnected." });
      setConfirmRow(null);
      await refresh();
    } catch (e) {
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Disconnect failed." });
    } finally { setBusyId(null); }
  }

  async function handleIntervalChange(id, syncInterval) {
    setUpdatingId(id);
    try {
      await IntegrationsService.updateSchedule(id, syncInterval);
      setConnected((prev) => prev.map((c) => (c.id === id ? { ...c, syncInterval } : c)));
    } catch (e) {
      setSnackbar({ severity: "error", message: "Failed to update schedule." });
    } finally { setUpdatingId(null); }
  }

  async function handleSignOut() {
    try { await ApiService.signout(); } catch {}
    navigate("/signin");
  }

  return (
    <AppShell account={account} onSignOut={handleSignOut} title="Data sources">
      <Stack spacing={3.5} sx={{ maxWidth: 1100 }}>

        {/* Page header */}
        <Box>
          <Typography variant="h2">Data sources</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Connect platforms to pull metrics automatically on your configured schedule.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        )}

        {/* Provider cards */}
        <Grid container spacing={2.5}>
          {loading && providers.length === 0
            ? [0, 1, 2, 3, 4, 5].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper sx={{ p: 2.5, height: 150 }}>
                    <Skeleton variant="circular" width={44} height={44} sx={{ mb: 1.5 }} />
                    <Skeleton height={18} width="60%" sx={{ mb: 0.75 }} />
                    <Skeleton height={14} width="90%" />
                  </Paper>
                </Grid>
              ))
            : providers.map((p) => {
                const connectedCount = connected.filter((c) => c.provider === p.provider).length;
                return (
                  <Grid item xs={12} sm={6} md={4} key={p.provider}>
                    <ProviderCard
                      provider={p}
                      connectedCount={connectedCount}
                      isConnecting={connectingProvider === p.provider}
                      onConnect={handleConnect}
                    />
                  </Grid>
                );
              })}
        </Grid>

        <Divider />

        {/* Connected accounts */}
        {PROVIDER_GROUPS.map((group) => {
          const rows = connected.filter((r) => group.providers.includes(r.provider));
          if (!loading && rows.length === 0) return null;
          return (
            <Box key={group.label}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="h4" sx={{ color: "text.primary" }}>{group.label}</Typography>
                <Chip
                  label={`${rows.length} account${rows.length !== 1 ? "s" : ""}`}
                  size="small"
                  sx={{ height: 22, fontSize: "0.72rem", bgcolor: "#F1F5F9", color: "#64748B" }}
                />
              </Stack>
              {loading && rows.length === 0 ? (
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
              ) : (
                <Stack spacing={1.5}>
                  {rows.map((row) => (
                    <ConnectedRow
                      key={row.id}
                      row={row}
                      busyId={busyId}
                      updatingId={updatingId}
                      onRefresh={handleRefreshOne}
                      onDisconnect={(r) => setConfirmRow(r)}
                      onHistory={(r) => setHistoryAccount(r)}
                      onIntervalChange={handleIntervalChange}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          );
        })}

        <Divider />

        {/* Sync log */}
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h4">Sync log</Typography>
              <Typography variant="caption" color="text.secondary">Last 20 sync runs</Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
              onClick={refreshLogs}
              disabled={logsLoading}
            >
              Refresh
            </Button>
          </Stack>
          <Paper sx={{ overflow: "hidden" }}>
            <SyncLogTable logs={syncLogs} loading={logsLoading} />
          </Paper>
        </Box>

        <Box>
          <Button
            component={RouterLink}
            to="/"
            variant="text"
            size="small"
            sx={{ color: "text.secondary" }}
          >
            ← Back to overview
          </Button>
        </Box>
      </Stack>

      {/* Disconnect dialog */}
      <Dialog open={Boolean(confirmRow)} onClose={() => setConfirmRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Disconnect account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Historical data for <strong>{confirmRow?.displayName}</strong> will be removed and automatic syncing will stop. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmRow(null)} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            size="small"
            disabled={busyId === confirmRow?.id}
            onClick={() => handleDisconnect(confirmRow)}
          >
            {busyId === confirmRow?.id ? "Disconnecting…" : "Disconnect"}
          </Button>
        </DialogActions>
      </Dialog>

      <HistoryDrawer
        connectedAccount={historyAccount}
        open={Boolean(historyAccount)}
        onClose={() => setHistoryAccount(null)}
      />

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant="filled" sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </AppShell>
  );
}
